// @ts-nocheck
/**
 * componentValidator.ts — WP-007 Structural Validator
 *
 * Validates the structural shape of a single ComponentNode (received as unknown).
 * Does not check semantic validity (data refs, expression IDs, etc.).
 *
 * Pure TypeScript — no framework dependencies.
 */

import type { ValidationResult } from '../../diagnostics';
import { addIssue } from '../../diagnostics';
import type { LayoutChartType, QrCodeErrorCorrection } from '../../types/component';
import { QR_CODE_ERROR_CORRECTION_LEVELS } from '../../types/component';
import {
  LAYOUT_CHART_TYPES,
  MAX_CHART_SERIES,
  XY_MULTI_SERIES_CHART_TYPES,
  migrateChartPayloadInPlace,
} from '../../runtime/renderer/chartTypeMigration';
import { migrateTablePayloadInPlace } from '../../runtime/renderer/tablePayloadMigration';

// ── Local helper ────────────────────────────────────────────────────────────

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

// ── Known types ─────────────────────────────────────────────────────────────

/**
 * Known MVP component types that are structurally valid.
 * Matches the ComponentType union defined in types/component.ts.
 */
export const KNOWN_COMPONENT_TYPES = [
  'text',
  'number',
  'boolean',
  'date',
  'enum',
  'stringSelect',
  'image',
  'svg',
  'html',
  'qrCode',
  'rating',
  'color',
  'button',
  'icon',
  'badge',
  'progress',
  'timerDisplay',
  'slider',
  'group',
  'spacer',
  'dragHandle',
  'line',
  'customToken',
  'table',
  'chart',
  'listView',
] as const;

function isValueSourceShape(v: unknown): v is Record<string, unknown> {
  return (
    isPlainObject(v) &&
    (v.kind === 'static' || v.kind === 'binding' || v.kind === 'expression')
  );
}

/** Accepts legacy bare LayoutChartType string or ValueSource. */
function isLayoutChartTypeValue(raw: unknown): boolean {
  if (typeof raw === 'string' && LAYOUT_CHART_TYPES.has(raw as LayoutChartType)) {
    return true;
  }
  if (!isValueSourceShape(raw)) {
    return false;
  }
  if (raw.kind === 'static') {
    return (
      typeof raw.value === 'string' &&
      LAYOUT_CHART_TYPES.has(raw.value as LayoutChartType)
    );
  }
  return true;
}

/** Resolve a static layout chart type token when present; null for binding/expression. */
function resolveStaticLayoutChartType(raw: unknown): LayoutChartType | null {
  if (typeof raw === 'string' && LAYOUT_CHART_TYPES.has(raw as LayoutChartType)) {
    return raw as LayoutChartType;
  }
  if (
    isValueSourceShape(raw) &&
    raw.kind === 'static' &&
    typeof raw.value === 'string' &&
    LAYOUT_CHART_TYPES.has(raw.value as LayoutChartType)
  ) {
    return raw.value as LayoutChartType;
  }
  return null;
}

const KNOWN_COMPONENT_TYPE_SET = new Set<string>(KNOWN_COMPONENT_TYPES);

const MAX_GROUP_DEPTH = 10;

// ── Internal recursive implementation ───────────────────────────────────────

function _validateComponentStructure(
  component: unknown,
  path: string,
  result: ValidationResult,
  depth: number,
): ValidationResult {
  if (depth >= MAX_GROUP_DEPTH) {
    // Depth cap reached — skip silently to prevent stack overflow on adversarial input.
    return result;
  }

  // Check 1: component must be a plain object
  if (!isPlainObject(component)) {
    return addIssue(result, {
      severity: 'error',
      code: 'schema.invalid_field_type',
      message: `Component at ${path} must be a plain object`,
      path,
    });
  }

  const comp = component as Record<string, unknown>;

  // Check 2: id must be a non-empty string
  if (typeof comp.id !== 'string' || comp.id === '') {
    result = addIssue(result, {
      severity: 'error',
      code: 'schema.missing_required_field',
      message: `Component is missing required field 'id'`,
      path: `${path}.id`,
    });
  }

  // Check 3: type must be a non-empty string in KNOWN_COMPONENT_TYPES
  const compType = comp.type;
  const typeIsKnown =
    typeof compType === 'string' &&
    compType !== '' &&
    KNOWN_COMPONENT_TYPE_SET.has(compType);

  if (!typeIsKnown) {
    result = addIssue(result, {
      severity: 'error',
      code: 'schema.unknown_component_type',
      message: `Component type '${String(compType)}' is not a known component type`,
      path: `${path}.type`,
    });
  }

  // Check 4: layout must be a plain object
  if (!isPlainObject(comp.layout)) {
    result = addIssue(result, {
      severity: 'error',
      code: 'schema.missing_required_field',
      message: `Component is missing required field 'layout' (must be a plain object)`,
      path: `${path}.layout`,
    });
  } else {
    const layout = comp.layout as Record<string, unknown>;

    // Check 5: layout.position must be 'absolute'
    if (layout.position !== 'absolute') {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.invalid_field_type',
        message: `Component layout.position must be 'absolute'`,
        path: `${path}.layout.position`,
      });
    }

    // Check 6: layout x/y/width/height must all be numbers
    for (const coord of ['x', 'y', 'width', 'height'] as const) {
      if (typeof layout[coord] !== 'number') {
        result = addIssue(result, {
          severity: 'error',
          code: 'schema.invalid_field_type',
          message: `Component layout.${coord} must be a number`,
          path: `${path}.layout.${coord}`,
        });
      }
    }
  }

  // Table payload: migrate legacy single source → dual binds, require source or tableSource
  if (compType === 'table') {
    if (!isPlainObject(comp.table)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.missing_required_field',
        message: `Table component is missing required field 'table' (must be a plain object)`,
        path: `${path}.table`,
      });
    } else {
      const table = { ...(comp.table as Record<string, unknown>) };
      migrateTablePayloadInPlace(table);
      const hasListSource = isPlainObject(table.source);
      const hasTableSource = isPlainObject(table.tableSource);
      if (!hasListSource && !hasTableSource) {
        result = addIssue(result, {
          severity: 'error',
          code: 'schema.missing_required_field',
          message: `Table requires table.source or table.tableSource`,
          path: `${path}.table`,
        });
      }
    }
  }

  // Chart payload (DEC-056): migrate legacy Y → series[], then validate multi-series rules
  if (compType === 'chart') {
    if (!isPlainObject(comp.chart)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.missing_required_field',
        message: `Chart component is missing required field 'chart' (must be a plain object)`,
        path: `${path}.chart`,
      });
    } else {
      // Validate a migrated copy so legacy yColumn/ySource still pass.
      const chart = { ...(comp.chart as Record<string, unknown>) };
      migrateChartPayloadInPlace(chart);

      const chartTypeOk = isLayoutChartTypeValue(chart.chartType);
      if (!chartTypeOk) {
        result = addIssue(result, {
          severity: 'error',
          code: 'schema.invalid_field_type',
          message: `Chart chart.chartType must be a LayoutChartType or ValueSource thereof`,
          path: `${path}.chart.chartType`,
        });
      }
      // Legacy seriesMode is ignored (migrated at load/render); do not error.
      if (
        chart.orientation != null &&
        chart.orientation !== 'vertical' &&
        chart.orientation !== 'horizontal'
      ) {
        result = addIssue(result, {
          severity: 'error',
          code: 'schema.invalid_field_type',
          message: `Chart chart.orientation must be 'vertical' or 'horizontal'`,
          path: `${path}.chart.orientation`,
        });
      }

      const seriesRaw = chart.series;
      if (!Array.isArray(seriesRaw) || seriesRaw.length < 1) {
        result = addIssue(result, {
          severity: 'error',
          code: 'schema.missing_required_field',
          message: `Chart chart.series must be a non-empty array (at least one series)`,
          path: `${path}.chart.series`,
        });
      } else if (seriesRaw.length > MAX_CHART_SERIES) {
        result = addIssue(result, {
          severity: 'error',
          code: 'schema.invalid_field_type',
          message: `Chart chart.series length must be ≤ ${MAX_CHART_SERIES}`,
          path: `${path}.chart.series`,
        });
      } else {
        let hasSeriesYColumn = false;
        let hasSeriesYSource = false;
        let hasSecondaryAxis = false;

        for (let i = 0; i < seriesRaw.length; i++) {
          const entry = seriesRaw[i];
          const seriesPath = `${path}.chart.series[${i}]`;
          if (!isPlainObject(entry)) {
            result = addIssue(result, {
              severity: 'error',
              code: 'schema.invalid_field_type',
              message: `Chart series entry must be a plain object`,
              path: seriesPath,
            });
            continue;
          }
          if (typeof entry.id !== 'string' || entry.id.trim() === '') {
            result = addIssue(result, {
              severity: 'error',
              code: 'schema.missing_required_field',
              message: `Chart series entry requires a non-empty string id`,
              path: `${seriesPath}.id`,
            });
          }
          if (
            entry.yAxis != null &&
            entry.yAxis !== 'primary' &&
            entry.yAxis !== 'secondary'
          ) {
            result = addIssue(result, {
              severity: 'error',
              code: 'schema.invalid_field_type',
              message: `Chart series yAxis must be 'primary' or 'secondary'`,
              path: `${seriesPath}.yAxis`,
            });
          }
          if (entry.yAxis === 'secondary') {
            hasSecondaryAxis = true;
          }
          if (typeof entry.yColumn === 'string' && entry.yColumn.trim() !== '') {
            hasSeriesYColumn = true;
          }
          if (isPlainObject(entry.ySource)) {
            hasSeriesYSource = true;
          }
        }

        const resolvedType = resolveStaticLayoutChartType(chart.chartType);
        if (
          resolvedType != null &&
          !XY_MULTI_SERIES_CHART_TYPES.has(resolvedType) &&
          (seriesRaw.length > 1 || hasSecondaryAxis)
        ) {
          result = addIssue(result, {
            severity: 'error',
            code: 'schema.invalid_field_type',
            message: `Chart multi-series / secondary Y is only allowed for line, scatter, and timeline (got '${resolvedType}')`,
            path: `${path}.chart.series`,
          });
        }

        const hasListSource = isPlainObject(chart.source);
        const hasTableSource = isPlainObject(chart.tableSource);
        // Shared X (xColumn / xSource) is optional.
        // DEC-078: chart.source (list/matrix) is a valid data path; series chrome may omit yColumn/ySource.
        if (!hasListSource && !hasTableSource && !hasSeriesYSource) {
          result = addIssue(result, {
            severity: 'error',
            code: 'schema.missing_required_field',
            message: `Chart requires chart.source, chart.tableSource, or a series entry with ySource`,
            path: `${path}.chart`,
          });
        }
        // tableSource column path: require yColumn/ySource unless list-backed source wins at paint.
        if (
          hasTableSource &&
          !hasListSource &&
          !hasSeriesYColumn &&
          !hasSeriesYSource
        ) {
          result = addIssue(result, {
            severity: 'error',
            code: 'schema.missing_required_field',
            message: `Chart series entry requires yColumn (or ySource) when using tableSource`,
            path: `${path}.chart.series`,
          });
        }
      }
    }
  }

  if (compType === 'qrCode') {
    if (!isPlainObject(comp.qrCode)) {
      result = addIssue(result, {
        severity: 'error',
        code: 'schema.missing_required_field',
        message: `QR code component is missing required field 'qrCode' (must be a plain object)`,
        path: `${path}.qrCode`,
      });
    } else {
      const qr = comp.qrCode as Record<string, unknown>;
      if (qr.source == null) {
        result = addIssue(result, {
          severity: 'error',
          code: 'schema.missing_required_field',
          message: `QR code requires qrCode.source`,
          path: `${path}.qrCode.source`,
        });
      } else if (!isValueSourceShape(qr.source)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'schema.invalid_field_type',
          message: `QR code qrCode.source must be a ValueSource`,
          path: `${path}.qrCode.source`,
        });
      }
      if (qr.color != null && !isValueSourceShape(qr.color)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'schema.invalid_field_type',
          message: `QR code qrCode.color must be a ValueSource`,
          path: `${path}.qrCode.color`,
        });
      }
      if (qr.backgroundColor != null && !isValueSourceShape(qr.backgroundColor)) {
        result = addIssue(result, {
          severity: 'error',
          code: 'schema.invalid_field_type',
          message: `QR code qrCode.backgroundColor must be a ValueSource`,
          path: `${path}.qrCode.backgroundColor`,
        });
      }
      if (
        qr.errorCorrection != null &&
        (typeof qr.errorCorrection !== 'string' ||
          !QR_CODE_ERROR_CORRECTION_LEVELS.includes(qr.errorCorrection as QrCodeErrorCorrection))
      ) {
        result = addIssue(result, {
          severity: 'error',
          code: 'schema.invalid_field_type',
          message: `QR code qrCode.errorCorrection must be L, M, Q, or H`,
          path: `${path}.qrCode.errorCorrection`,
        });
      }
      if (
        qr.fit != null &&
        qr.fit !== 'contain' &&
        qr.fit !== 'cover' &&
        qr.fit !== 'fill'
      ) {
        result = addIssue(result, {
          severity: 'error',
          code: 'schema.invalid_field_type',
          message: `QR code qrCode.fit must be contain, cover, or fill`,
          path: `${path}.qrCode.fit`,
        });
      }
    }
  }

  // Recursive group children (depth-limited)
  if (
    compType === 'group' &&
    isPlainObject(comp.group) &&
    Array.isArray((comp.group as Record<string, unknown>).children)
  ) {
    const children = (comp.group as Record<string, unknown>).children as unknown[];
    for (let k = 0; k < children.length; k++) {
      result = _validateComponentStructure(
        children[k],
        `${path}.group.children[${k}]`,
        result,
        depth + 1,
      );
    }
  }

  return result;
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Validates the structural shape of a single component node.
 * Adds issues to the result and returns the updated result.
 * Recursively validates group children (depth-capped at 10 levels).
 */
export function validateComponentStructure(
  component: unknown,
  path: string,
  result: ValidationResult,
): ValidationResult {
  return _validateComponentStructure(component, path, result, 0);
}
